import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  BellIcon,
  EnvelopeIcon,
  PhoneIcon,
  CloudArrowUpIcon,
  PlusIcon,
  TrashIcon,
  InformationCircleIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import {
  getTenants,
  getNotificationContacts,
  updateNotificationContacts,
  sendTestNotification,
  getIntegrations,
  createIntegration,
  updateIntegration,
} from '../services/tenant.service';
import { useAuthStore } from '../store/authStore';
import { useLicense } from '../hooks/useLicense';
import { phoneError } from '../utils/phone';
import { plural } from '../utils/format';
import PhoneField from '../components/common/PhoneField';
import { splitE164 } from '../utils/countryCodes';

const TIER_LABELS = { starter: 'Starter', growth: 'Growth', scale: 'Scale' };

// Normalised form of everything the save bar covers, for spotting changes.
const snapshotOf = ({ emails, phones, prefs, webhookEnabled, webhookUrl }) => JSON.stringify({
  emails: (emails || []).map((e) => ({ email: e.email || '', name: e.name || '' })),
  phones: (phones || []).map((p) => ({ phone: p.phone || '', name: p.name || '' })),
  prefs: {
    missed: prefs?.missed_lead_reminders_enabled !== false,
    callback: prefs?.widget_request_callback_enabled !== false,
    helpdesk: prefs?.widget_helpdesk_enabled !== false,
  },
  webhook: JSON.stringify({ enabled: Boolean(webhookEnabled), url: (webhookUrl || '').trim() }),
});

export default function NotificationSettingsPage() {
  const { tenantId: urlTenantId } = useParams();
  const { hasFeature } = useLicense();
  const user = useAuthStore((s) => s.user);
  const isSuperadmin = user?.is_superuser || user?.role === 'superadmin';

  // Superadmin needs to pick a tenant; regular users use their own
  const [tenantList, setTenantList] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(urlTenantId || (!isSuperadmin ? user?.tenant_id : ''));
  const tenantId = selectedTenantId;

  const [loading, setLoading] = useState(true);
  const [tenantsLoading, setTenantsLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [emails, setEmails] = useState([]);
  const [phones, setPhones] = useState([]);
  const [tierInfo, setTierInfo] = useState({
    tier: 'growth',
    included_emails: 3,
    included_phones: 2,
    notification_config: {},
  });
  const [webhookIntegrationId, setWebhookIntegrationId] = useState(null);
  const [webhookEnabled, setWebhookEnabled] = useState(false);
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSaving, setWebhookSaving] = useState(false);
  // missed_lead_reminders_enabled: per-tenant opt-out for Growth/Scale tenants.
  // Defaults to true (on) when key absent. Starter ignores this entirely.
  const [missedLeadRemindersEnabled, setMissedLeadRemindersEnabled] = useState(true);
  const [widgetRequestCallbackEnabled, setWidgetRequestCallbackEnabled] = useState(true);
  const [widgetHelpdeskEnabled, setWidgetHelpdeskEnabled] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  // One save for the whole page. There were two buttons -- one for contacts
  // at the bottom, one for the webhook mid-page -- and nothing said which
  // changes each one kept. The bar appears only when something is unsaved.
  const [savedSnapshot, setSavedSnapshot] = useState(null);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  // { type: 'email'|'phone', index: number, value: string }

  // ── Load tenant list for superadmin ────────────────────────────
  useEffect(() => {
    if (!isSuperadmin) return;
    const loadTenants = async () => {
      try {
        setTenantsLoading(true);
        const data = await getTenants(true); // active tenants only
        setTenantList(data);
        // Auto-select first tenant if none selected
        if (!selectedTenantId && data.length > 0) {
          setSelectedTenantId(data[0].id);
        }
      } catch (err) {
        console.error('Failed to load tenants:', err);
        toast.error('Failed to load tenants');
      } finally {
        setTenantsLoading(false);
      }
    };
    loadTenants();
  }, [isSuperadmin]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Load notification contacts ────────────────────────────────
  const loadContacts = useCallback(async () => {
    if (!tenantId) {
      setWebhookIntegrationId(null);
      setWebhookUrl('');
      setWebhookEnabled(false);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const data = await getNotificationContacts(tenantId);
      setEmails(data.notification_emails || []);
      setPhones(data.notification_phones || []);
      const prefs = data.notification_preferences || {};
      setMissedLeadRemindersEnabled(prefs.missed_lead_reminders_enabled !== false);
      setWidgetRequestCallbackEnabled(prefs.widget_request_callback_enabled !== false);
      setWidgetHelpdeskEnabled(prefs.widget_helpdesk_enabled !== false);
      setTierInfo({
        tier: data.tier,
        included_emails: data.included_emails,
        included_phones: data.included_phones,
        notification_config: data.notification_config || {},
      });
      try {
        const integrations = await getIntegrations(tenantId, 'crm');
        const leadWebhook = (integrations || []).find((integration) =>
          ['webhook', 'custom_webhook'].includes(integration.provider)
        );
        setWebhookIntegrationId(leadWebhook?.id || null);
        setWebhookUrl(leadWebhook?.base_url || '');
        setWebhookEnabled(Boolean(leadWebhook?.is_active));
        setSavedSnapshot(snapshotOf({
          emails: data.notification_emails || [],
          phones: data.notification_phones || [],
          prefs,
          webhookEnabled: Boolean(leadWebhook?.is_active),
          webhookUrl: leadWebhook?.base_url || '',
        }));
      } catch (integrationErr) {
        console.error('Failed to load lead webhook integration:', integrationErr);
        setWebhookIntegrationId(null);
        setWebhookUrl('');
        setWebhookEnabled(false);
        // Contacts loaded fine; without a snapshot the save bar never appears.
        setSavedSnapshot(snapshotOf({
          emails: data.notification_emails || [],
          phones: data.notification_phones || [],
          prefs,
          webhookEnabled: false,
          webhookUrl: '',
        }));
      }
    } catch (err) {
      console.error('Failed to load notification contacts:', err);
      toast.error('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadContacts(); }, [loadContacts]);

  // ── Save ──────────────────────────────────────────────────────
  const currentSnapshot = snapshotOf({
    emails,
    phones,
    prefs: {
      missed_lead_reminders_enabled: missedLeadRemindersEnabled,
      widget_request_callback_enabled: widgetRequestCallbackEnabled,
      widget_helpdesk_enabled: widgetHelpdeskEnabled,
    },
    webhookEnabled,
    webhookUrl,
  });
  const dirty = savedSnapshot !== null && currentSnapshot !== savedSnapshot;
  const webhookDirty = savedSnapshot !== null
    && JSON.parse(savedSnapshot).webhook !== JSON.parse(currentSnapshot).webhook;

  const handleSave = async () => {
    // Validate
    for (const entry of emails) {
      if (!entry.email || !/\S+@\S+\.\S+/.test(entry.email)) {
        toast.error(`Invalid email: ${entry.email || '(empty)'}`);
        return;
      }
    }
    for (const entry of phones) {
      const err = phoneError(entry.phone);
      if (err) {
        toast.error(`${entry.phone || '(empty)'}: ${err}`);
        return;
      }
    }
    try {
      setSaving(true);
      const response = await updateNotificationContacts(tenantId, {
        notification_emails: emails,
        notification_phones: phones,
        notification_preferences: {
          missed_lead_reminders_enabled: missedLeadRemindersEnabled,
          widget_request_callback_enabled: widgetRequestCallbackEnabled,
          widget_helpdesk_enabled: widgetHelpdeskEnabled,
        },
      });
      if (webhookDirty && webhookAllowed) {
        const webhookSaved = await handleSaveWebhook();
        if (!webhookSaved) return;
      }
      setSavedSnapshot(currentSnapshot);
      toast.success(response.message || 'Changes saved');
    } catch (err) {
      console.error('Save failed:', err);
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error(detail, { duration: 5000 });
      } else {
        toast.error('Failed to save notification contacts');
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Helpers ───────────────────────────────────────────────────
  const addEmail = () => {
    if (emails.length >= tierInfo.included_emails) return;
    setEmails(prev => [...prev, { email: '', name: '' }]);
  };
  const updateEmail = (idx, field, value) =>
    setEmails(prev => prev.map((e, i) => (i === idx ? { ...e, [field]: value } : e)));

  const addPhone = () => {
    if (phones.length >= tierInfo.included_phones) return;
    setPhones(prev => [...prev, { phone: '', name: '' }]);
  };
  const updatePhone = (idx, field, value) =>
    setPhones(prev => prev.map((p, i) => (i === idx ? { ...p, [field]: value } : p)));

  // WhatsApp numbers go straight to Meta, which rejects numbers without a
  // country code. Block saving rather than storing a number that can never
  // be delivered to.
  const hasInvalidPhone = phones.some(p => phoneError(p.phone));
  // A new number most likely shares the country of the ones already saved.
  const savedPhoneCountry = phones.map((p) => splitE164(p.phone || '').iso).find(Boolean) || '';

  const nc = tierInfo.notification_config;
  // The Integrations add-on on any plan, or Scale, which includes it.
  const webhookAllowed = hasFeature('integrations');

  const handleSaveWebhook = async () => {
    if (!tenantId) return false;
    if (!webhookAllowed) {
      toast.error('Lead webhook push is part of the Integrations add-on (included in Scale).');
      return false;
    }

    const trimmedUrl = webhookUrl.trim();
    if (webhookEnabled && !/^https?:\/\//i.test(trimmedUrl)) {
      toast.error('Enter a valid webhook URL starting with http:// or https://');
      return false;
    }

    try {
      setWebhookSaving(true);
      if (webhookIntegrationId) {
        const updated = await updateIntegration(tenantId, webhookIntegrationId, {
          display_name: 'Lead Webhook Push',
          base_url: trimmedUrl,
          is_active: webhookEnabled,
        });
        setWebhookUrl(updated.base_url || trimmedUrl);
        setWebhookEnabled(Boolean(updated.is_active));
      } else {
        const created = await createIntegration(tenantId, {
          integration_type: 'crm',
          provider: 'webhook',
          display_name: 'Lead Webhook Push',
          base_url: trimmedUrl,
          sync_enabled: false,
          is_active: webhookEnabled,
        });
        setWebhookIntegrationId(created.id);
        setWebhookUrl(created.base_url || trimmedUrl);
        setWebhookEnabled(Boolean(created.is_active));
      }
      return true;
    } catch (err) {
      console.error('Failed to save lead webhook:', err);
      toast.error(err?.response?.data?.detail || 'Failed to save lead webhook');
      return false;
    } finally {
      setWebhookSaving(false);
    }
  };

  const handleTestAlert = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      setTestResult(await sendTestNotification(tenantId));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send a test alert');
    } finally {
      setTesting(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────
  if (loading || tenantsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
      </div>
    );
  }

  const selectedTenantName = tenantList.find(t => t.id === tenantId)?.name;

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="flex items-center gap-3">
        <BellIcon className="h-8 w-8 text-indigo-600" />
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Lead alerts</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {isSuperadmin
              ? 'Choose where a workspace receives new lead alerts.'
              : 'Choose who hears about a new lead, by email and WhatsApp.'}
          </p>
        </div>
      </div>

      {/* Tenant selector for superadmin */}
      {isSuperadmin && (
        <div className="rounded-lg bg-white border border-gray-200 shadow-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <BuildingOfficeIcon className="h-5 w-5 text-gray-500" />
            <label htmlFor="tenant-select" className="text-sm font-semibold text-gray-700">
              Select Tenant
            </label>
          </div>
          <select
            id="tenant-select"
            value={selectedTenantId}
            onChange={(e) => setSelectedTenantId(e.target.value)}
            className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
          >
            <option value="">— Select a tenant —</option>
            {tenantList.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} ({t.slug}) — {(t.subscription_tier || 'growth').charAt(0).toUpperCase() + (t.subscription_tier || 'growth').slice(1)}
              </option>
            ))}
          </select>
        </div>
      )}

      {!tenantId ? (
        <div className="text-center py-12 text-gray-400">
          <BuildingOfficeIcon className="h-12 w-12 mx-auto mb-3 text-gray-300" />
          <p className="text-lg font-medium">Select a tenant above to manage their notification contacts.</p>
        </div>
      ) : (
      <>
      {/* Tier info banner */}
      <div className="rounded-lg bg-indigo-50 border border-indigo-200 p-4 flex items-start gap-3">
        <InformationCircleIcon className="h-5 w-5 text-indigo-600 mt-0.5 flex-shrink-0" />
        <div className="text-sm text-indigo-800">
          <p className="font-medium">
            {selectedTenantName && <span>{selectedTenantName} — </span>}
            {TIER_LABELS[tierInfo.tier] || tierInfo.tier} Plan
          </p>
          <ul className="mt-1 space-y-0.5 list-disc list-inside text-indigo-700">
            {nc.realtime_email && <li>Real-time email notification on every new lead</li>}
            {nc.realtime_whatsapp && <li>Real-time WhatsApp notification on every new lead</li>}
            {nc.digest_email && <li>Weekly email digest every Sunday at 9:00 AM IST</li>}
            {!nc.realtime_whatsapp && !nc.digest_email && !nc.realtime_email && (
              <li>No lead notifications configured for this tier</li>
            )}
          </ul>
          <p className="mt-2 text-xs">
            Your plan includes <strong>{plural(tierInfo.included_emails, 'email address', 'email addresses')}</strong> and{' '}
            <strong>{plural(tierInfo.included_phones, 'WhatsApp number')}</strong>.
            Upgrade your plan for more contacts.
          </p>
        </div>
      </div>

      {/* ─── Email Contacts ─────────────────────────────────── */}
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <EnvelopeIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Notification Emails</h2>
            <span className="text-xs text-gray-400">
              ({emails.length}/{tierInfo.included_emails} included)
            </span>
          </div>
          <button
            onClick={addEmail}
            disabled={emails.length >= tierInfo.included_emails}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition ${
              emails.length >= tierInfo.included_emails
                ? 'text-gray-400 bg-gray-100 cursor-not-allowed'
                : 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
            }`}
            title={emails.length >= tierInfo.included_emails ? `Your plan includes ${plural(tierInfo.included_emails, 'email address', 'email addresses')}` : 'Add email'}
          >
            <PlusIcon className="h-4 w-4" /> Add Email
          </button>
        </div>

        <div className="p-5 space-y-3">
          {emails.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No addresses added yet. Until you add one, alerts go to your workspace admins&rsquo; email.
            </p>
          )}
          {emails.map((entry, idx) => (
            <div key={idx} className="flex items-center gap-3">
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                <input
                  type="email"
                  placeholder="email@example.com"
                  value={entry.email || ''}
                  onChange={(e) => updateEmail(idx, 'email', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
                />
                <input
                  type="text"
                  placeholder="Name (optional)"
                  value={entry.name || ''}
                  onChange={(e) => updateEmail(idx, 'name', e.target.value)}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
                />
              </div>
              {idx < tierInfo.included_emails && (
                <span className="text-xs text-green-600 flex items-center gap-1 w-16">
                  <CheckCircleIcon className="h-4 w-4" /> OK
                </span>
              )}
              <button
                onClick={() => setDeleteConfirm({ type: 'email', index: idx, value: entry.email })}
                className="text-red-400 hover:text-red-600 p-1"
                title="Remove"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      </section>

      {/* ─── WhatsApp Contacts ──────────────────────────────── */}
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <PhoneIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">WhatsApp Numbers</h2>
            <span className="text-xs text-gray-400">
              ({phones.length}/{tierInfo.included_phones} included)
            </span>
          </div>
          <button
            onClick={addPhone}
            disabled={!nc.realtime_whatsapp || phones.length >= tierInfo.included_phones}
            className={`inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-md transition ${
              nc.realtime_whatsapp && phones.length < tierInfo.included_phones
                ? 'text-indigo-600 bg-indigo-50 hover:bg-indigo-100'
                : 'text-gray-400 bg-gray-100 cursor-not-allowed'
            }`}
            title={
              !nc.realtime_whatsapp
                ? 'WhatsApp not available on this plan'
                : phones.length >= tierInfo.included_phones
                ? `Your plan includes ${plural(tierInfo.included_phones, 'WhatsApp number')}`
                : 'Add WhatsApp number'
            }
          >
            <PlusIcon className="h-4 w-4" /> Add Number
          </button>
        </div>

        <div className="p-5 space-y-3">
          {!nc.realtime_whatsapp && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-700">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
              WhatsApp notifications are not available on the {TIER_LABELS[tierInfo.tier]} plan.
              Upgrade to Growth or Scale for real-time WhatsApp alerts.
            </div>
          )}
          {nc.realtime_whatsapp && phones.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No WhatsApp number yet, so nobody gets new leads on WhatsApp. Add the number your team checks most.
            </p>
          )}
          {phones.map((entry, idx) => {
            const err = phoneError(entry.phone);
            return (
              <div key={idx} className="flex items-start gap-3">
                <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <PhoneField
                      value={entry.phone || ''}
                      defaultCountryIso={savedPhoneCountry}
                      onChange={(next) => updatePhone(idx, 'phone', next)}
                      selectClassName="w-36 shrink-0 rounded-md border border-gray-300 bg-white px-2 py-2 text-sm shadow-sm focus:ring-indigo-500 focus:border-indigo-500"
                      inputClassName={`min-w-0 flex-1 rounded-md shadow-sm text-sm px-3 py-2 border ${
                        err
                          ? 'border-red-400 focus:ring-red-500 focus:border-red-500'
                          : 'border-gray-300 focus:ring-indigo-500 focus:border-indigo-500'
                      }`}
                    />
                    {err && <p className="text-xs text-red-500 mt-1">{err}</p>}
                  </div>
                  <input
                    type="text"
                    placeholder="Name (optional)"
                    value={entry.name || ''}
                    onChange={(e) => updatePhone(idx, 'name', e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border"
                  />
                </div>
                <span className="text-xs flex items-center gap-1 w-16 pt-2">
                  {err ? (
                    <span className="text-red-500 flex items-center gap-1">
                      <ExclamationTriangleIcon className="h-4 w-4" /> Fix
                    </span>
                  ) : (
                    <span className="text-green-600 flex items-center gap-1">
                      <CheckCircleIcon className="h-4 w-4" /> OK
                    </span>
                  )}
                </span>
                <button
                  onClick={() => setDeleteConfirm({ type: 'phone', index: idx, value: entry.phone })}
                  className="text-red-400 hover:text-red-600 p-1 pt-2"
                  title="Remove"
                >
                  <TrashIcon className="h-4 w-4" />
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* Prove alerts arrive, without waiting for a real lead */}
      <section className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
        <h2 className="text-lg font-semibold text-gray-900">Test your alerts</h2>
        <p className="mt-1 text-sm text-gray-500">
          Sends one sample lead, marked as a test, to the addresses and numbers saved above.
        </p>
        <button
          type="button"
          onClick={handleTestAlert}
          disabled={testing || dirty}
          className="mt-3 inline-flex min-h-[44px] items-center rounded-lg border border-indigo-600 px-4 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {testing ? 'Sending…' : 'Send test alert'}
        </button>
        {dirty && <p className="mt-2 text-xs text-gray-500">Save your changes first, so the test goes to them.</p>}
        {testResult && (
          <ul className="mt-3 space-y-1 text-sm" role="status">
            <li className={testResult.email.sent ? 'text-green-700' : 'text-red-700'}>
              {testResult.email.sent
                ? `Email sent to ${plural(testResult.email.recipients, 'address', 'addresses')}.`
                : testResult.email.recipients
                  ? 'The email could not be sent. Check the addresses and try again.'
                  : 'No email address to send to.'}
            </li>
            <li className={testResult.whatsapp.sent ? 'text-green-700' : 'text-gray-700'}>
              {!testResult.whatsapp.available
                ? 'WhatsApp alerts are not included on your plan.'
                : testResult.whatsapp.sent
                  ? `WhatsApp sent to ${plural(testResult.whatsapp.recipients, 'number')}.`
                  : testResult.whatsapp.recipients
                    ? 'The WhatsApp alert could not be sent. Check the numbers and try again.'
                    : 'No WhatsApp number to send to. Add one above.'}
            </li>
          </ul>
        )}
      </section>

      {/* Widget action visibility */}
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <BellIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Widget Action Buttons</h2>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Choose which action buttons visitors see in the AI chat widget.
          </p>
        </div>

        <div className="p-5 space-y-3">
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={widgetRequestCallbackEnabled}
              onChange={(e) => setWidgetRequestCallbackEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <span>
              <span className="font-medium text-gray-900">Request Callback</span>
              <span className="block text-xs text-gray-500">Shows the standard lead capture form.</span>
            </span>
          </label>
          <label className="flex items-start gap-3 text-sm">
            <input
              type="checkbox"
              checked={widgetHelpdeskEnabled}
              onChange={(e) => setWidgetHelpdeskEnabled(e.target.checked)}
              className="mt-0.5 h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            <span>
              <span className="font-medium text-gray-900">Helpdesk ticket</span>
              <span className="block text-xs text-gray-500">Shows the support ticket pill for existing customers when support is enabled.</span>
            </span>
          </label>
        </div>
      </section>

      {/* Lead Webhook Push */}
      <section className="bg-white rounded-lg border border-gray-200 shadow-sm">
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <CloudArrowUpIcon className="h-5 w-5 text-gray-500" />
            <h2 className="text-lg font-semibold text-gray-900">Lead Webhook Push</h2>
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 font-semibold">Integrations add-on</span>
          </div>
          <p className="mt-1 text-sm text-gray-500">
            Push every captured lead to a CRM, Google Sheet connector, automation tool, or custom webhook endpoint.
          </p>
        </div>

        <div className="p-5 space-y-4">
          {!webhookAllowed && (
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md text-sm text-amber-700">
              <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
              Lead webhook push is part of the Integrations add-on (included in Scale). Add it from Billing or talk to us.
            </div>
          )}

          <label className="flex items-center gap-2 text-sm font-medium text-gray-900">
            <input
              type="checkbox"
              checked={webhookEnabled}
              disabled={!webhookAllowed}
              onChange={(e) => setWebhookEnabled(e.target.checked)}
              className="h-4 w-4 text-indigo-600 border-gray-300 rounded"
            />
            Enable webhook push for new leads
          </label>

          <div>
            <label htmlFor="lead-webhook-url" className="block text-sm font-medium text-gray-700 mb-1">
              Webhook URL
            </label>
            <input
              id="lead-webhook-url"
              type="url"
              placeholder="https://example.com/serviti/leads"
              value={webhookUrl}
              disabled={!webhookAllowed}
              onChange={(e) => setWebhookUrl(e.target.value)}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:ring-indigo-500 focus:border-indigo-500 text-sm px-3 py-2 border disabled:bg-gray-100 disabled:text-gray-500"
            />
            <p className="mt-1 text-xs text-gray-500">
              Serviti sends a JSON payload with lead details, request label, preferred time, agent, and source conversation.
            </p>
          </div>

        </div>
      </section>

      {/* ─── Missed-lead reminders opt-out (Growth/Scale only) ── */}
      {nc?.missed_lead_reminders && (
        <section className="rounded-lg bg-white border border-gray-200 shadow-sm p-6">
          <div className="flex items-start gap-3 mb-4">
            <BellIcon className="h-6 w-6 text-amber-600 mt-0.5" />
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Automated Follow-up Reminders</h2>
              <p className="text-sm text-gray-500 mt-1">
                When a lead is captured but not marked contacted within 2 hours, Serviti re-pings your team
                on email and WhatsApp. A second nudge fires at 24 hours if it's still uncontacted.
                Included on {TIER_LABELS[tierInfo.tier] || tierInfo.tier} plans.
              </p>
            </div>
          </div>
          <label className="inline-flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={missedLeadRemindersEnabled}
              onChange={(e) => setMissedLeadRemindersEnabled(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-gray-800">
              {missedLeadRemindersEnabled
                ? 'Enabled — your team will be nudged at 2h and 24h'
                : 'Disabled — only the real-time alert at capture fires'}
            </span>
          </label>
        </section>
      )}

      {/* ─── Save bar: only when something is unsaved ─────────── */}
      {dirty && (
      <div className="sticky bottom-0 z-10 -mx-2 flex flex-wrap items-center justify-end gap-3 rounded-lg border border-indigo-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur dark:border-indigo-800 dark:bg-gray-800/95">
        <p className="mr-auto text-sm font-medium text-gray-700 dark:text-gray-200">You have unsaved changes.</p>
        {hasInvalidPhone && (
          <p className="text-sm text-red-500">
            Fix the WhatsApp numbers above before saving.
          </p>
        )}
        <button
          onClick={handleSave}
          disabled={saving || webhookSaving || hasInvalidPhone}
          title={hasInvalidPhone ? 'Every WhatsApp number needs a country code, e.g. +919876543210' : undefined}
          className="inline-flex items-center gap-2 px-6 py-2.5 rounded-lg bg-indigo-600 text-white font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition"
        >
          {saving || webhookSaving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
              Saving...
            </>
          ) : (
            'Save changes'
          )}
        </button>
      </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-sm w-full mx-4 shadow-xl">
            <h3 className="font-semibold text-gray-900 dark:text-white mb-2">Remove Contact?</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              Remove <strong>{deleteConfirm.value}</strong> from notifications? They will no longer receive lead alerts.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  if (deleteConfirm.type === 'email') {
                    setEmails(emails.filter((_, i) => i !== deleteConfirm.index));
                  } else {
                    setPhones(phones.filter((_, i) => i !== deleteConfirm.index));
                  }
                  setDeleteConfirm(null);
                }}
                className="px-4 py-2 text-sm bg-red-600 text-white rounded hover:bg-red-700 transition"
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      </>
      )}
    </div>
  );
}
